import React, { useState } from "react";
import { useSyncWorkItemCalendar } from "./hooks/useSyncWorkItemCalendar";
import {
  Sync as SyncIcon,
  Error as ErrorIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  IconButton,
  Tooltip,
  Badge,
  Popover,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

// Hjälpfunktion för att extrahera ett begripligt felmeddelande
const getErrorMessage = (error: FetchBaseQueryError | SerializedError | undefined): string => {
  if (!error) return "Okänt fel";
  
  // FetchBaseQueryError från RTK Query
  if ('status' in error) {
    // Hantera de vanligaste feltyperna
    if (typeof error.status === 'number') {
      const statusCode = error.status;
      if (statusCode === 401) return "Ej behörig (401)";
      if (statusCode === 403) return "Åtkomst nekad (403)";
      if (statusCode === 404) return "Resursen hittades inte (404)";
      if (statusCode === 500) return "Serverfel (500)";
      return `Fel: ${statusCode}`;
    }
    
    // Om det finns ett data-objekt med ett error-fält
    if (error.data && typeof error.data === 'object' && 'message' in error.data) {
      return error.data.message as string;
    }
    
    // Om det finns ett eget felmeddelande
    if (error.error) {
      return error.error;
    }
    
    return JSON.stringify(error.data) || "Nätverksfel";
  }
  
  // SerializedError från createAsyncThunk
  if ('message' in error && error.message) {
    return error.message;
  }
  
  if ('code' in error && error.code) {
    return `Felkod: ${error.code}`;
  }
  
  return "Något gick fel";
};

// Kompakt komponent för att visa synkstatus som ikon
const WorkItemCalendarSync: React.FC = () => {
  const { isLoading, error, lastSyncResult, lastServerSyncResult } =
    useSyncWorkItemCalendar();
  
  // State för popover
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Hantera klick för att visa/dölja detaljinformation
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Visa popover endast om det finns fel eller varningar
    if (error || (lastServerSyncResult && lastServerSyncResult.failedCount > 0)) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Bestäm ikon och färg baserat på status
  const getStatusIcon = () => {
    if (isLoading) {
      return <CircularProgress size={20} />;
    }
    
    if (error) {
      return (
        <Badge color="error" variant="dot">
          <ErrorIcon color="error" />
        </Badge>
      );
    }
    
    if (lastServerSyncResult && lastServerSyncResult.failedCount > 0) {
      return (
        <Badge badgeContent={lastServerSyncResult.failedCount} color="warning">
          <SyncIcon color="warning" />
        </Badge>
      );
    }
    
    if (lastSyncResult && 
        (lastSyncResult.added.length > 0 || 
         lastSyncResult.modified.length > 0 || 
         lastSyncResult.deleted.length > 0)) {
      return <CheckIcon color="success" />;
    }
    
    return <SyncIcon color="action" />;
  };

  // Tooltip-text baserat på status
  const getTooltipText = () => {
    if (isLoading) {
      return "Synkroniserar kalender...";
    }
    
    if (error) {
      return "Fel vid kalendersynkronisering - klicka för detaljer";
    }
    
    if (lastServerSyncResult && lastServerSyncResult.failedCount > 0) {
      return `${lastServerSyncResult.failedCount} misslyckade synkroniseringar - klicka för detaljer`;
    }
    
    if (lastSyncResult) {
      const total = lastSyncResult.added.length + lastSyncResult.modified.length + lastSyncResult.deleted.length;
      if (total > 0) {
        return `Senaste synk: ${total} kalenderhändelser uppdaterade`;
      }
    }
    
    return "Kalendersynkronisering aktiv";
  };

  // Formatera tidsstämplar
  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <>
      <Tooltip title={getTooltipText()}>
        <IconButton 
          onClick={handleClick} 
          size="small"
          sx={{ 
            color: error ? 'error.main' : 'inherit',
            animation: isLoading ? 'spin 2s linear infinite' : 'none',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        >
          {getStatusIcon()}
        </IconButton>
      </Tooltip>

      {/* Popover för detaljinformation vid fel eller varningar */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon fontSize="small" color="info" />
              Synkroniseringsstatus
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {error && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'error.light', borderRadius: 1, color: 'error.contrastText' }}>
              <Typography variant="body2" gutterBottom fontWeight="medium">
                Fel vid synkronisering
              </Typography>
              <Typography variant="caption">
                {getErrorMessage(error)}
              </Typography>
            </Box>
          )}

          {lastSyncResult && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" gutterBottom>
                Senaste kalendersynkronisering:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="caption">Nya händelser: {lastSyncResult.added.length}</Typography>
                <Typography variant="caption">Ändrade händelser: {lastSyncResult.modified.length}</Typography>
                <Typography variant="caption">Borttagna händelser: {lastSyncResult.deleted.length}</Typography>
              </Box>
            </Box>
          )}

          {lastServerSyncResult && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Server-synkronisering ({formatTime(lastServerSyncResult.lastSyncTime)}):
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Typography variant="caption">Lyckade: {lastServerSyncResult.updatedCount}</Typography>
                  <Typography variant="caption" color={lastServerSyncResult.failedCount > 0 ? 'error' : 'inherit'}>
                    Misslyckade: {lastServerSyncResult.failedCount}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            Automatisk synkronisering är aktiv
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default WorkItemCalendarSync;